import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSearchParams,useNavigate } from "react-router-dom";
import styles from "./MediaTabs.module.css";
import mediaData from "../../../json/mediaData.json";
import navbarData from "../../../json/navbarData.json";

const { cardsData } = mediaData;
const { megaContent } = navbarData;

const sectionCategoriesMap = {
  news: megaContent.news.items.map((i) => i.title),
  science: megaContent.science.items.map((i) => i.title),
  corporate: megaContent.corporate.items.map((i) => i.title),
  regulatory: megaContent.regulatory.items.map((i) => i.title),
};

// Keep the newsroom heading aligned with the section opened from the menu.
const sectionHeadings = Object.fromEntries(
  Object.entries(megaContent).map(([slug, content]) => [slug, content.label]),
);

const allCategories = ["All", ...Object.values(sectionCategoriesMap).flat()];

const ITEMS_PER_PAGE = 6;

const slugToCategory = (slug, section) => {
  if (!slug || !sectionCategoriesMap[section]) return null;
  return sectionCategoriesMap[section].find(
    (cat) =>
      cat
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/&/g, "")
        .replace(/--+/g, "-") === slug,
  );
};

const MediaTabs = () => {
  const tabsRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const section = searchParams.get("section") || "news";
  const categorySlug = searchParams.get("category");
  const heading = sectionHeadings[section] || "Media Coverage";

  const navigate = useNavigate();

  const sectionSlug = searchParams.get("section");
  const visibleCategories = useMemo(() => {
    if (section && sectionCategoriesMap[section]) {
      return ["All", ...sectionCategoriesMap[section]];
    }
    return allCategories;
  }, [section]);


  useEffect(() => {
    if (categorySlug) {
      const matched = slugToCategory(categorySlug, section);
      setActiveTab(matched || "All");
    } else {
      setActiveTab("All");
    }
    setCurrentPage(1);
  }, [section, categorySlug]);

  const filteredCards = useMemo(() => {
    let cards = cardsData;

    if (activeTab === "All") {
      const sectionCategories = sectionCategoriesMap[section] || [];
      return cards.filter((item) => sectionCategories.includes(item.category));
    }

    cards = cards.filter((item) => item.category === activeTab);
    return cards;
  }, [activeTab, section]);

  const totalPages = Math.ceil(filteredCards.length / ITEMS_PER_PAGE);

  const paginatedData = filteredCards.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleTabChange = (tab, e) => {
    setActiveTab(tab);
    setCurrentPage(1);

    if (e?.currentTarget) {
      e.currentTarget.scrollIntoView({
        behavior: "smooth",
        inline: "start",   
        block: "nearest",
      });
    }
  };

  const generatePages = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };
  const renderCategory = (text) => {
    const parts = text.split(/(&|-)/g);

    return (
      <>
        {parts.map((part, index) => {
          if (part === "&") {
            return (
              <span key={index} className={styles.diffentFamily}>
                &
              </span>
            );
          }
          if (part === "-") {
            return (
              <span key={index} className={styles.hyphenFamily}>
                -
              </span>
            );
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };

  const renderTitle = (text) => {
    const parts = text.split(/(['’])/g);

    return (
      <>
        {parts.map((part, index) =>
          part === "'" || part === "’" ? (
            <span key={index} className={styles.apostropheFamily}>
              {"’"}
            </span>
          ) : (
            <span key={index}>{part}</span>
          ),
        )}
      </>
    );
  };

  const renderHeading = (text) => {
    const [beforeAmpersand, ...afterAmpersand] = text.split("&");

    if (afterAmpersand.length === 0) return text;

    return (
      <>
        {beforeAmpersand}
        <span className={styles.headingAmpersand}>&amp;</span>
        {afterAmpersand.join("&")}
      </>
    );
  };

  return (
    <div className={styles.wrapper}>
      <h2 className={styles.heading}>{renderHeading(heading)}</h2>
      <div className={styles.tabs}>
        {visibleCategories.map((tab) => (
          <button
            key={tab}
            className={`${styles.tab} ${activeTab === tab ? styles.active : ""}`}
            onClick={(e) => handleTabChange(tab, e)}
          >
            {renderCategory(tab)}
          </button>
        ))}
      </div>

      {/* Cards */}
      <div className={styles.grid}>
        {paginatedData.length > 0 ? (
          paginatedData.map((card) => (
            <div key={card.id} className={styles.card} onClick={() =>
                navigate(
                  `/blog-detail?section=${sectionSlug}`,
                )}>
              <img src={card.img} alt={card.title} loading="lazy" decoding="async" />
              <div className={styles.cardBody} >
                <span className={styles.badge}>
                  {renderCategory(card.category)}
                </span>
                <h3>{renderTitle(card.title)}</h3>
                <h4>{card.subtitle}</h4>
                <p>{card.desc}</p>
                <button className={styles.buttonreadButton}>~ Read More</button>
                <div className={styles.divider}></div>
                <div className={styles.date}>{card.date}</div>
              </div>
            </div>
          ))
        ) : (
          <p className={styles.noData}>No articles found in this category.</p>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => prev - 1)}
            className={styles.navBtn}
          >
            ← Previous
          </button>
          <div className={styles.pageNumbers}>
            {generatePages().map((page, index) =>
              page === "..." ? (
                <span key={index} className={styles.dots}>
                  ...
                </span>
              ) : (
                <button
                  key={index}
                  onClick={() => setCurrentPage(page)}
                  className={`${styles.pageBtn} ${currentPage === page ? styles.pageActive : ""}`}
                >
                  {page}
                </button>
              ),
            )}
          </div>
          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((prev) => prev + 1)}
            className={styles.navBtn}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default MediaTabs;
